import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ContentfulService } from 'src/services/contentful.service';
import { ACTIVATION_ROUTE_URLS, PLANS_SHOP_ROUTE_URLS, SHOP_ROUTE_URLS, SUPPORT_ROUTE_URLS } from '../app.routes.names';
import { Observable, Subscription, debounceTime, distinctUntilChanged, of, switchMap } from 'rxjs';
import { IDeviceCompatibilityV1, IExistingOrder, IUser, IUserPlan, MobileCustomPlansServiceNoSync } from '@ztarmobile/zwp-service-backend';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { AppState } from '../app.service';
import { AddressesAutocompleteService, EquipmentService, IAutoCompletePrediction } from '@ztarmobile/zwp-service-backend-v2';
import { ModalHelper } from 'src/services/modal-helper.service';
import { ToastrHelperService } from 'src/services/toast-helper.service';
import { CAPTCHA_BOT_ERROR_CODE, PARAM_ERROR_MESSAGES, RED } from '../app.config';
import { AnimationTriggerMetadata, animate, style, transition, trigger } from '@angular/animations';
import { AddressAutoCompleteHelperService } from '@services/address-auto-complete.service';

@Component({
  selector: 'app-bring-phone',
  templateUrl: './bring-phone.component.html',
  styleUrls: ['./bring-phone.component.scss'],
  animations: [fadeSlide(300)]
})

export class BringPhoneComponent implements OnInit, OnDestroy {
  public compatibilityForm: FormGroup;
  public SUPPORT_ROUTE_URLS = SUPPORT_ROUTE_URLS;
  public firstThreeQuestions;
  public restQuestions;
  public questionId: string;
  public collapsed = false;
  public showMore = false;
  public displayedAddressModel: any;
  public invalidAddress = false;
  public filteredOptions: Observable<Array<IAutoCompletePrediction>>;
  public address: any;
  public equipment = '';
  public zipCode = '';
  public userPlanId: string;
  public user: IUser;
  public withDevice = false;
  public captchaResponse: string;
  public compatibleDevice: IDeviceCompatibilityV1;
  public userPlan: IUserPlan;
  public simValidity: IExistingOrder;
  public calculatedHeight;
  public defaultSIM;
  public showCompatibilityform = false;
  public showGetStarted = true;
  public recaptchaResolvedSubscription: Subscription;


  constructor(
    public router: Router,
    private contentfulService: ContentfulService,
    private placesAutoCompleteService: AddressesAutocompleteService,
    private appState: AppState,
    private equipmentService: EquipmentService,
    public modalHelper: ModalHelper,
    public toastHelper: ToastrHelperService,
    public mobileCustomPlansService: MobileCustomPlansServiceNoSync,
    public cdr: ChangeDetectorRef,
    private addressAutoCompleteService: AddressAutoCompleteHelperService
    ) {

      this.compatibilityForm = new FormGroup({
        address: new FormControl('', [Validators.required, this.addressAutoCompleteService.addressObjectValidator]),
        imei: new FormControl('', Validators.compose([Validators.required, Validators.minLength(11), Validators.maxLength(18)]))
      })
  }

  ngOnInit(): void {
    this.contentfulService.getQuestionsByCategoryId('faqs', 'byod').subscribe(questions => {
      if (!!questions) {
        const allQuestions = questions[0].fields.questions;
        this.firstThreeQuestions = allQuestions.slice(0, 3);
        this.restQuestions = allQuestions.slice(3);
      }
    });

    const sessionId = sessionStorage.getItem('placesSessionToken');
    this.filteredOptions = this.compatibilityForm.controls.address.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        if (typeof value !== 'string' || value.trim().length === 0) {
          return of([]); // Clear the autocomplete suggestions
        }
        return this.placesAutoCompleteService.findAddress(value, sessionId);
      })
    );
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    if (this.recaptchaResolvedSubscription) {
      this.recaptchaResolvedSubscription.unsubscribe();
    }
  }
  public checkAddress(): ValidatorFn {
    return (c) => of(this.addressAutoCompleteService.addressObjectValidator(c))
  }

  public onPaste(event: any): void {
    event.preventDefault();
  }

  public showCheckCompatibility() {
    this.showCompatibilityform = true;
    this.showGetStarted = false;
    this.cdr.detectChanges();
  }

  public hideCheckCompatibility() {
    this.compatibilityForm.reset();
    this.showCompatibilityform = false;
    setTimeout(() => {
      this.showGetStarted = true;
    }, 300)
    this.cdr.detectChanges();
  }

  public displayFn(option: IAutoCompletePrediction): string {
    return option?.description || '';
  }

  public addressSelected(selectedAddress: IAutoCompletePrediction): void {
    this.appState.loading = true;

    this.addressAutoCompleteService
      .addressDetails(selectedAddress, this.displayedAddressModel, selectedAddress.main_text)
      .subscribe({
        next: (result) => {
          this.displayedAddressModel = result.model;
          this.address = result.formatted;
          this.invalidAddress = false;
          this.appState.loading = false;
        },
        error: (err) => {
          this.invalidAddress = true;
          this.appState.loading = false;
          console.warn('Failed to resolve address details', err);
        }
      });
  }

  public checkYourPhone(): void {
    this.router.navigate([`${ACTIVATION_ROUTE_URLS.BASE}/${ACTIVATION_ROUTE_URLS.CHECK_PHONE}`]);
  }

  public goToPlans(month?: number): void {
    this.router.navigate([`${SHOP_ROUTE_URLS.BASE}/${SHOP_ROUTE_URLS.PLANS_AND_FEATURES}/${PLANS_SHOP_ROUTE_URLS.NEW_PLAN}`], {
      queryParams: { month }
    });
  }

  public toggleActive(targetId, answerId): void {
    if (this.questionId === targetId && !this.collapsed) {
      this.questionId = 'q0';
    } else {
      this.questionId = targetId;
      this.collapsed = false;
      this.callRichText(answerId);
    }
  }

  public populatePhoneDetails(phoneDetails) {
    this.compatibleDevice = phoneDetails.details as IDeviceCompatibilityV1;
    this.compatibleDevice.manufacturer = phoneDetails?.details?.make;
    this.compatibleDevice.marketingName = phoneDetails?.details?.name;
    this.compatibleDevice.address1 = this.displayedAddressModel?.address1;
    this.compatibleDevice.address2 = this.displayedAddressModel?.address2;
    this.compatibleDevice.city = this.displayedAddressModel?.city;
    this.compatibleDevice.state = this.displayedAddressModel?.state;
    this.compatibleDevice.postalCode = this.displayedAddressModel?.postalCode;
    this.compatibleDevice.id = phoneDetails?.details?.serialNumber;
  }

  public handleCompatibilityError(error) {
    this.appState.loading = false;
    this.recaptchaResolvedSubscription?.unsubscribe();
    const errorDetails = error?.error?.errors[0];
    let errorMessage = errorDetails?.message;
    
    if(errorDetails?.code !== CAPTCHA_BOT_ERROR_CODE) {    
      if (errorDetails?.code === 102) {
        const paramName = errorDetails.params[0]?.param_name;
        const paramMessage = errorDetails.params[0]?.message;
  
        if (paramName.includes('equipment') || paramName.includes('imei')) {
          errorMessage = PARAM_ERROR_MESSAGES.IMEI;
        } else if (paramMessage === 'Invalid zip code') {
          errorMessage = PARAM_ERROR_MESSAGES.ADDRESS;
        }
      }
  
      errorMessage = errorMessage || errorDetails?.message || PARAM_ERROR_MESSAGES.GENERIC;
  
      const customHTML = `<div class="message" >
          <p>${errorMessage}</p>
        </div>`;
    this.modalHelper.showInformationMessageModal('OOPS!', '', 'Try Again', null, true, 'check-compatibility-modal', customHTML, undefined, undefined, undefined, undefined, { top: '250px' });
    } else if(errorDetails?.code === CAPTCHA_BOT_ERROR_CODE) {
      this.modalHelper.showRecaptchaVerificationModal().afterClosed().subscribe((result) => {
        if (!!result) {
          this.appState.loading = true;
          this.callCheckCoverageApi(result, false);
        }
      });
     }
  }

  public checkPhoneCompatibility(): void {
    this.compatibilityForm.markAllAsTouched();
    const action = 'freeup_check_coverage_by_imei';
    if (!!this.compatibilityForm.valid &&!!this.displayedAddressModel) {
      this.appState.loading = true;
      this.appState.resetAndExecuteCaptchaSubject.next({action});
      this.recaptchaResolvedSubscription= this.appState.resolvedCaptcha.subscribe(response => {
        this.captchaResponse = response;
        if(!!this.captchaResponse) {
          this.recaptchaResolvedSubscription?.unsubscribe();
          this.equipment = this.compatibilityForm.controls.imei.value;
          if (this.equipment.indexOf(' ') >= 0) {
            this.equipment = this.equipment.replace(/\s+/g, '');
          }
          this.zipCode = this.displayedAddressModel?.postalCode;
          this.callCheckCoverageApi(this.captchaResponse, true, action);
        }
      });
    }
  }
  public callCheckCoverageApi(captchaRes, invisibleCaptchaRes, action?): void {
    this.equipmentService.checkNetworkCoverageByImei(captchaRes, this.displayedAddressModel?.postalCode,
      this.displayedAddressModel?.address1, this.displayedAddressModel?.city,
      this.displayedAddressModel?.state,this.equipment,this.displayedAddressModel?.address2, null,!!action?action:null, invisibleCaptchaRes).then(res => {
        if (!!res) {
          this.appState.loading = false;
          this.defaultSIM = this.getDefaultSIM(res);
          if (!!this.defaultSIM) { // covered
            this.populatePhoneDetails(this.defaultSIM);
            const customHTML = `<div class="message">
              <p>Your device is ready and able to join our network!</p>
              <p>Make sure your device is unlocked to work on the FreeUp Mobile Network.</p>
            </div>`;
            this.modalHelper.showInformationMessageModal('Congratulations!', '', 'Shop Plans', null, true, 'check-compatibility-modal', customHTML, undefined, undefined, undefined, undefined, { top: '250px' })
              .afterClosed().subscribe(res => {
                this.mobileCustomPlansService.setPlanDevice(this.compatibleDevice);
                const isESIM = !!this.defaultSIM.details.eSimCapable;
                this.mobileCustomPlansService.seteSIM(isESIM);
                if (res)
                  this.router.navigate([`${SHOP_ROUTE_URLS.BASE}/${SHOP_ROUTE_URLS.PLANS_AND_FEATURES}/${PLANS_SHOP_ROUTE_URLS.NEW_PLAN}`]);
              });
          } else {
            let errorMessage = PARAM_ERROR_MESSAGES.NETWORK_MISMATCH;
            const customHTML = `<div class="message" >
          <p>${errorMessage}</p>
        </div>`;
            this.modalHelper.showInformationMessageModal('OOPS!', '', 'Try Again', null, true, 'check-compatibility-modal', customHTML, undefined, undefined, undefined, undefined, { top: '250px' });
            return;
          }
        }
      }, (error) => {
        this.handleCompatibilityError(error);
      }).catch((error)=>{
        this.handleCompatibilityError(error);
      })
  }
  public getDefaultSIM(res) {
    let defaultSIM;
    if (!!res?.equipmentCoverage?.defaultSim) {
      defaultSIM = res.equipmentCoverage.defaultSim;
    } else {
      const simCovered = res?.equipmentCoverage?.simOptions.some((i) => i.network === RED);
      const esimCapable = res?.equipmentCoverage?.simOptions[0].details.eSimCapable; // we need to check if we have to default to esim or physical sim based on the device
      if (!!simCovered) {
        const simOption = res?.equipmentCoverage?.simOptions.find((i) => i.network === RED && (!!esimCapable ? i.simType === 'esim' : i.simType === 'sim'));
        defaultSIM = simOption;
      } else {
        defaultSIM = null;
      }
    }
    return defaultSIM
  }

  private callRichText(id): void {
    this.contentfulService.getRichText('questions', id);
  }
}

export function fadeSlide(timing: number): AnimationTriggerMetadata {
  return trigger('fadeSlide', [
    transition(':enter', [
      style({ opacity: 0, transform: 'translateX(-100%)' }),
      animate(
        timing,
        style({ opacity: 1, transform: 'translateY(0)' })
      )
    ]),
    transition(':leave', [
      style({ opacity: 1, transform: 'translateY(0)' }),
      animate(
        timing,
        style({ opacity: 0, transform: 'translateX(100%)' })
      )
    ])
  ]);
}
